import {
  Directive,
  ElementRef,
  HostListener,
  AfterViewInit,
} from '@angular/core';

@Directive({
  selector: '[dialogEllipsisTooltip]',
  standalone: true,
})
export class DialogEllipsisTooltipDirective implements AfterViewInit {
  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.updateTooltipAttribute();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateTooltipAttribute();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    // Re-evaluate on hover and show floating tooltip if truncated
    const el = this.elementRef.nativeElement;
    const isTruncated = el.scrollWidth > el.clientWidth;

    console.log('Dialog Tooltip - Mouse Enter', {
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
      isTruncated,
      text: el.textContent?.trim(),
    });

    if (isTruncated) {
      const text = el.textContent?.trim() ?? '';
      if (text) {
        console.log('Showing tooltip with text:', text);
        this.showFloatingTooltip(text, el);
      }
    } else {
      this.hideFloatingTooltip();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hideFloatingTooltip();
  }

  private updateTooltipAttribute(): void {
    const el = this.elementRef.nativeElement;
    // guard: ensure layout calculated
    const isTruncated = el.scrollWidth > el.clientWidth;
    if (!isTruncated) this.hideFloatingTooltip();
  }

  private tooltipEl: HTMLDivElement | null = null;

  private showFloatingTooltip(text: string, anchor: HTMLElement): void {
    console.log('showFloatingTooltip called', { text, anchor });

    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'dialog-global-tooltip';
      console.log('Tooltip element created');
    }
    this.tooltipEl.textContent = text;

    const rect = anchor.getBoundingClientRect();
    const tooltipMaxWidth = 280;

    console.log('Anchor rect:', rect);

    // Check if we're inside a dialog
    const dialog = anchor.closest(
      '.mat-mdc-dialog-container, .cdk-overlay-pane',
    );
    console.log('Dialog found:', dialog);

    if (dialog) {
      // Append tooltip to dialog instead of body for proper positioning
      if (!dialog.contains(this.tooltipEl)) {
        dialog.appendChild(this.tooltipEl);
        console.log('Tooltip appended to dialog');
      }

      // We're inside a dialog - position using absolute positioning
      const dialogRect = dialog.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();

      console.log('Dialog rect:', dialogRect);

      // Calculate position relative to dialog
      let top = anchorRect.bottom - dialogRect.top + 4;
      let left = anchorRect.left - dialogRect.left;

      // Ensure tooltip doesn't overflow dialog on the right
      if (left + tooltipMaxWidth > dialogRect.width) {
        left = dialogRect.width - tooltipMaxWidth - 10;
      }

      // Ensure tooltip doesn't overflow dialog on the left
      if (left < 0) {
        left = 10;
      }

      console.log('Tooltip position:', { top, left });

      // Apply all styles inline to ensure they take precedence
      this.tooltipEl.style.position = 'absolute';
      this.tooltipEl.style.top = `${top}px`;
      this.tooltipEl.style.left = `${left}px`;
      this.tooltipEl.style.maxWidth = `${tooltipMaxWidth}px`;
      this.tooltipEl.style.zIndex = '2147483647'; // Maximum z-index value
      this.tooltipEl.style.padding = '10px 8px';
      this.tooltipEl.style.borderRadius = '4px';
      this.tooltipEl.style.boxShadow = '0 0 4px 0 rgba(30, 30, 30, 0.8)';
      this.tooltipEl.style.backgroundColor = '#fff';
      this.tooltipEl.style.color = '#0033a0';
      this.tooltipEl.style.fontSize = '10px';
      this.tooltipEl.style.wordWrap = 'break-word';
      this.tooltipEl.style.whiteSpace = 'normal';
      this.tooltipEl.style.lineHeight = '1.4';
      this.tooltipEl.style.overflowWrap = 'break-word';
      this.tooltipEl.style.pointerEvents = 'none'; // Don't block mouse events
    }

    this.tooltipEl.style.display = 'block';
    console.log('Tooltip display set to block. Final computed styles:', {
      display: this.tooltipEl.style.display,
      zIndex: this.tooltipEl.style.zIndex,
      position: this.tooltipEl.style.position,
      top: this.tooltipEl.style.top,
      left: this.tooltipEl.style.left,
    });

    // Verify element is in DOM
    console.log(
      'Tooltip in DOM:',
      document.body.contains(this.tooltipEl) ||
        (dialog && dialog.contains(this.tooltipEl)),
    );
    console.log('Tooltip element:', this.tooltipEl);
  }

  private hideFloatingTooltip(): void {
    if (this.tooltipEl) {
      this.tooltipEl.style.display = 'none';
    }
  }
}
